import { Form, Input } from 'antd';
import { useHistory } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { Mail, ArrowLeft, KeyRound, Lock } from 'lucide-react';

import callApi from '../../Api/config';
import { toastError, toastSuccess } from '../../service/Toast';
import { getLoginErrorMessage } from '../Login/loginErrors';
import { FORGOT_PASSWORD, RESET_PASSWORD, VERIFY_PASSWORD_OTP } from '../../constants/EntityName';
import { InvertFillButton } from '../../components/InvertFillButton';
import { REGEX_PASSWORD } from '../../utils/Regexp/Regex';

import './style.scss';

const NETWORK_ERROR = 'Could not reach the server. Please check your connection and try again.';
const PASSWORD_RULE_MESSAGE =
  'Use at least 8 characters with an uppercase letter, a lowercase letter, a number and a symbol (@$!%*?&#^()).';

type Step = 'email' | 'otp' | 'password';

/** Shows the backend message, or a network message when there was no response. */
const errorText = (response: any) =>
  response?.data ? getLoginErrorMessage(response.data.message) : NETWORK_ERROR;

/**
 * CMS "Forgot password?": email a 6-digit code -> verify it in the CMS ->
 * create the new password in the same card.
 */
const ForgotPassword = () => {
  const history = useHistory();
  const [passwordForm] = Form.useForm();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [resetToken, setResetToken] = useState<string | null>(null);
  // A ref, not the state above: two clicks in the same frame both read the
  // pre-update `loading` value and would each fire a request (and email).
  const isSubmittingRef = useRef(false);

  const normalizedEmail = email.trim().toLowerCase();

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn(seconds => seconds - 1), 1000);

    return () => clearTimeout(timer);
  }, [resendIn]);

  const run = async (action: () => Promise<void>) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setLoading(true);
    try {
      await action();
    } catch {
      toastError(NETWORK_ERROR);
    }
    isSubmittingRef.current = false;
    setLoading(false);
  };

  const sendCode = () =>
    run(async () => {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
        toastError('Please enter a valid email address.');
        return;
      }
      const response: any = await callApi(FORGOT_PASSWORD, 'POST', { email: normalizedEmail });
      if (!response?.status) {
        toastError(errorText(response));
        return;
      }
      toastSuccess(response.data?.message || 'A 6-digit code has been sent to your email.');
      setOtp('');
      setStep('otp');
      setResendIn(response.data?.data?.resendAfterSeconds || 60);
    });

  const verifyCode = () =>
    run(async () => {
      if (!/^\d{6}$/.test(otp)) {
        toastError('Please enter the 6-digit code from your email.');
        return;
      }
      const response: any = await callApi(VERIFY_PASSWORD_OTP, 'POST', {
        email: normalizedEmail,
        otp,
      });
      const token = response?.data?.data?.resetToken;
      if (!response?.status || !token) {
        toastError(errorText(response));
        return;
      }
      toastSuccess('Code verified. Please create your new password.');
      setResetToken(token);
      setStep('password');
    });

  const savePassword = () =>
    run(async () => {
      let values: { newPassword: string };
      try {
        values = await passwordForm.validateFields();
      } catch {
        return;
      }
      const response: any = await callApi(RESET_PASSWORD, 'POST', {
        resetToken,
        newPassword: values.newPassword,
      });
      if (!response?.status) {
        toastError(errorText(response));
        // An expired / used verification cannot be retried: start over.
        if (response?.data) {
          passwordForm.resetFields();
          setResetToken(null);
          setStep('email');
        }
        return;
      }
      toastSuccess(response.data?.message || 'Password updated successfully. You can now sign in.');
      setResetToken(null);
      history.push('/login');
    });

  return (
    <div className="auth-page auth-page--standalone">
      <div className="auth-page__card auth-page__card--wide">
        <button type="button" className="auth-back-link" onClick={() => history.push('/login')}>
          <ArrowLeft size={16} strokeWidth={2} />
          <span>Back to sign in</span>
        </button>

        <div className="auth-page__header">
          <h2 className="auth-page__title">Reset your password</h2>
          <p className="auth-page__subtitle">
            {step === 'email'
              ? 'Enter the email of your CMS account and we will send you a 6-digit verification code.'
              : step === 'otp'
                ? `Enter the 6-digit code we sent to ${normalizedEmail}. It expires in 10 minutes.`
                : `Code verified. Create a new password for ${normalizedEmail}.`}
          </p>
        </div>

        {step === 'password' ? (
          <Form form={passwordForm} className="auth-form" layout="vertical" onFinish={savePassword}>
            <Form.Item
              name="newPassword"
              label="Create password"
              className="auth-form__item"
              rules={[
                { required: true, message: 'Please create a new password.' },
                { pattern: REGEX_PASSWORD, message: PASSWORD_RULE_MESSAGE },
              ]}
            >
              <Input.Password
                className="auth-form__input"
                style={{ borderRadius: '8px' }}
                placeholder="Create a new password"
                prefix={<Lock size={18} strokeWidth={2} className="auth-form__icon" />}
                size="large"
                autoComplete="new-password"
              />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label="Re-enter password"
              className="auth-form__item"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Please re-enter the new password.' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) return Promise.resolve();

                    return Promise.reject(new Error('Passwords do not match.'));
                  },
                }),
              ]}
            >
              <Input.Password
                className="auth-form__input"
                style={{ borderRadius: '8px' }}
                placeholder="Re-enter the new password"
                prefix={<Lock size={18} strokeWidth={2} className="auth-form__icon" />}
                size="large"
                autoComplete="new-password"
              />
            </Form.Item>

            <div className="auth-form__submit-row">
              <InvertFillButton
                variant="blue"
                size="lg"
                fontSize="lg"
                fontWeight="semibold"
                className="auth-form__submit-btn"
                onClick={savePassword}
                disabled={loading}
              >
                <span>{loading ? 'Saving…' : 'Save new password'}</span>
              </InvertFillButton>
            </div>
          </Form>
        ) : (
          <Form
            className="auth-form"
            layout="vertical"
            onFinish={step === 'email' ? sendCode : verifyCode}
          >
            {step === 'email' ? (
              <Form.Item label="Email address" className="auth-form__item">
                <Input
                  className="auth-form__input"
                  style={{ borderRadius: '8px' }}
                  placeholder="you@company.com"
                  prefix={<Mail size={18} strokeWidth={2} className="auth-form__icon" />}
                  size="large"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="email"
                  value={email}
                  onChange={(e: any) => setEmail(e.target.value)}
                />
              </Form.Item>
            ) : (
              <Form.Item label="Verification code" className="auth-form__item">
                <Input
                  className="auth-form__input"
                  style={{ borderRadius: '8px', letterSpacing: '6px' }}
                  placeholder="000000"
                  prefix={<KeyRound size={18} strokeWidth={2} className="auth-form__icon" />}
                  size="large"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={otp}
                  onChange={(e: any) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </Form.Item>
            )}

            <div className="auth-form__submit-row">
              <InvertFillButton
                variant="blue"
                size="lg"
                fontSize="lg"
                fontWeight="semibold"
                className="auth-form__submit-btn"
                onClick={step === 'email' ? sendCode : verifyCode}
                disabled={loading}
              >
                <span>
                  {step === 'email'
                    ? loading
                      ? 'Sending code…'
                      : 'Send code'
                    : loading
                      ? 'Verifying…'
                      : 'Verify code'}
                </span>
              </InvertFillButton>
            </div>
          </Form>
        )}

        <div className="auth-page__footer">
          {step === 'password' ? (
            <span className="auth-page__footer-muted">
              <button
                type="button"
                className="auth-back-link"
                onClick={() => {
                  passwordForm.resetFields();
                  setResetToken(null);
                  setStep('email');
                }}
              >
                Start over
              </button>
            </span>
          ) : step === 'email' ? (
            <span className="auth-page__footer-muted">
              Didn&apos;t get the email? Check your spam folder or try again.
            </span>
          ) : (
            <span className="auth-page__footer-muted">
              {resendIn > 0 ? (
                `Resend code in ${resendIn}s`
              ) : (
                <button
                  type="button"
                  className="auth-back-link"
                  onClick={sendCode}
                  disabled={loading}
                >
                  Resend code
                </button>
              )}
              {' · '}
              <button
                type="button"
                className="auth-back-link"
                onClick={() => {
                  setStep('email');
                  setOtp('');
                }}
              >
                Use a different email
              </button>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
